import * as React from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ChatBubbleOutlineRounded, CloseRounded, ConfirmationNumberOutlined } from '@mui/icons-material'
import { SupportAlert, useSupportAlerts } from '../../hook/useSupportAlerts'
import { useAdminTheme } from '../../hook/useAdminTheme'
import l from '../../page/SubscribersPageV2.module.css'
import h from './HeaderV2.module.css'

/*
 * A pop-up in the bottom-right corner for each new support alert (a ticket,
 * a customer's comment) — a click opens the ticket in support_v2. Gone by
 * itself after a few seconds; up to three at a time.
 */

const SHOW_MS = 6000

export function SupportToasts() {
  const navigate = useNavigate()
  const { theme } = useAdminTheme()
  const [shown, setShown] = React.useState<SupportAlert[]>([])
  const timers = React.useRef<Record<number, number>>({})

  const hide = React.useCallback((id: number) => {
    window.clearTimeout(timers.current[id])
    delete timers.current[id]
    setShown((list) => list.filter((a) => a.id !== id))
  }, [])

  useSupportAlerts((a) => {
    setShown((list) => [a, ...list.filter((x) => x.id !== a.id)].slice(0, 3))
    timers.current[a.id] = window.setTimeout(() => hide(a.id), SHOW_MS)
  })

  React.useEffect(() => () => Object.values(timers.current).forEach((t) => window.clearTimeout(t)), [])

  if (!shown.length) return null
  // out of the menu's drawer, on top of the page
  return createPortal(
    <div className={`${l.theme} ${h.toasts}`} data-theme={theme}>
      {shown.map((a) => (
        <div
          key={a.id}
          className={`${h.toast} ${a.kind === 'ticket' ? h.toastIsTicket : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => { hide(a.id); navigate(`/support_v2?ticket=${a.ticketId}`) }}
          onKeyDown={(e) => { if (e.key === 'Enter') { hide(a.id); navigate(`/support_v2?ticket=${a.ticketId}`) } }}
        >
          <span className={`${h.toastIcon} ${a.kind === 'ticket' ? h.toastTicket : ''}`}>
            {a.kind === 'ticket' ? <ConfirmationNumberOutlined sx={{ fontSize: 18 }} /> : <ChatBubbleOutlineRounded sx={{ fontSize: 17 }} />}
          </span>
          <span className={h.toastText}>
            <b>{a.title}</b>
            {a.text && <span>{a.text}</span>}
          </span>
          <button type="button" className={h.close} onClick={(e) => { e.stopPropagation(); hide(a.id) }} aria-label="Close"><CloseRounded sx={{ fontSize: 16 }} /></button>
        </div>
      ))}
    </div>,
    document.body,
  )
}
